import { FC, useEffect, useState } from 'react';
import { Crown, Clock } from 'lucide-react';
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useStripePayment } from '@/hooks/useStripePayment';
import { useAuth } from '@/hooks/useAuth';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { differenceInDays, addDays } from 'date-fns';

interface SubscriptionButtonProps {
  profile: {
    trial_used: boolean;
    trial_expired: boolean;
    trial_start_date: string;
    subscription_status: string;
  };
}

export const SubscriptionButton: FC<SubscriptionButtonProps> = ({ profile }) => {
  const [remainingDays, setRemainingDays] = useState<number>(0);
  const { handlePayment, isLoading } = useStripePayment();
  const { user } = useAuth();

  useEffect(() => {
    if (!profile) return;

    const calculateRemainingDays = () => {
      if (profile.trial_start_date && !profile.trial_expired) {
        const startDate = new Date(profile.trial_start_date);
        const trialEndDate = addDays(startDate, 7);
        const today = new Date();
        const days = differenceInDays(trialEndDate, today) + 1;
        setRemainingDays(Math.max(0, days));
      }
    };

    calculateRemainingDays();
    const interval = setInterval(calculateRemainingDays, 1000 * 60 * 60 * 24);
    return () => clearInterval(interval);
  }, [profile]);

  const handleSubscribe = async () => {
    if (!user?.id) {
      toast.error('Debes iniciar sesión para continuar');
      return;
    }

    try {
      await handlePayment(user.id);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Error al procesar el pago');
      }
    }
  };

  const isInTrial = remainingDays > 0;

  return (
    <div className="flex flex-col items-end gap-1">
      {isInTrial && (
        <div className="text-xs text-gray-600 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          <span>{remainingDays} días restantes de prueba</span>
        </div>
      )}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleSubscribe}
              disabled={isLoading || profile?.subscription_status === 'active'}
              className={`
                px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2
                ${profile?.subscription_status === 'active' 
                  ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                  : 'bg-gradient-to-r from-primary via-secondary to-accent text-white hover:opacity-90'}
              `}
            >
              {isLoading ? (
                <>
                  <Clock className="h-4 w-4 animate-spin" />
                  <span className="hidden sm:inline">Procesando...</span>
                </>
              ) : profile?.subscription_status === 'active' ? (
                <>
                  <Crown className="h-4 w-4" />
                  <span className="hidden sm:inline">Premium Activo</span>
                </>
              ) : (
                <>
                  <Crown className="h-4 w-4" />
                  <span className="hidden sm:inline">Activar Premium</span>
                  {remainingDays > 0 && !profile?.trial_expired && (
                    <span className="hidden sm:inline text-xs opacity-90">({remainingDays}d)</span>
                  )}
                </>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {profile?.subscription_status === 'active'
              ? '¡Gracias por ser parte de nuestra comunidad premium!'
              : 'Obtén acceso ilimitado a todo el contenido'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
